import { z } from 'zod'

type DGELicenseProperty = {
  id: number
  name: string
  logoUrl: string | null
  sport: {
    id: number
    name: string
    color: string
  }
}

type DGELicensePeriod = {
  id?: number
  version: number
  purchasedEvents: number | null
  maxEvents: number | null
  start: string
  end: string
}

// /api/v1/operators/${operatorId} GET
export type DGEOperatorLicense = {
  id: number
  maxEvents: number
  createdAt: string
  expiresAt: string
  startAt: string
  geoList: string[]
  subGeoWhiteList: string[] | null
  subGeoList: string[] | null
  bookedEventsCount: number | null
  liveEventsCount: number | null
  property: DGELicenseProperty
  isWhiteList: boolean
  isDeleted: boolean
  purchasedEvents: number | null
  currentLicensingPeriodId: number
  geoWhiteList: string[]
}

// /api/v1/licenses/${licenseId} GET
export type DGELicense = {
  id: number
  operator: {
    id: number
  }
  maxEvents: number
  property: Pick<DGELicenseProperty, 'id' | 'name'>
  geoListField: string
  isWhiteList: boolean
  createdAt: string
  periods: DGELicensePeriod[]
  overflowEmailSent: string | null
  isDeleted: boolean
  maxBitrateAllowed: number
  allowLLP: boolean
  actualPeriod: null
  storeVod: boolean
  storePermanentVod: boolean
  allowLiveDVR: boolean
  allowApiVodAccess: boolean
  interactiveStreaming: boolean
  autobookingEnabled: boolean
}

export type FormattedDGELicensePeriod = Omit<DGELicensePeriod, 'start' | 'end'> &
  Pick<DGELicense, 'autobookingEnabled'> & { startDate: string; endDate: string }

export type FormattedDGELicense = Pick<
  DGELicense,
  | 'isDeleted'
  | 'maxBitrateAllowed'
  | 'allowLLP'
  | 'actualPeriod'
  | 'storeVod'
  | 'storePermanentVod'
  | 'allowLiveDVR'
  | 'allowApiVodAccess'
  | 'interactiveStreaming'
  | 'autobookingEnabled'
> &
  Pick<DGEOperatorLicense, 'isWhiteList' | 'maxEvents'> & {
    licenseId: number
    property: string
    propertyData: DGELicenseProperty
    licensingPeriods: FormattedDGELicensePeriod[]
    geoListField: string[]
  }

export type FormattedDGELicensePeriodWithProperty = FormattedDGELicensePeriod &
  Pick<FormattedDGELicense, 'property' | 'propertyData' | 'isDeleted' | 'licenseId'>

export type GroupedDGELicensePeriodWithProperty = Record<
  string,
  FormattedDGELicensePeriodWithProperty[]
>

export type DGELicensesMap = Record<string, FormattedDGELicense[]>

// /api/v1/licenses/${licenseId} POST Response
export type SavedStreamsLicenseResponse = Pick<
  DGEOperatorLicense,
  'id' | 'maxEvents' | 'createdAt' | 'isWhiteList' | 'isDeleted'
> & {
  geoListField: string
  periods: DGELicensePeriod[]
  maxBitrateAllowed: number
  allowLLP: boolean
  actualPeriod: boolean | null
  storeVod: boolean
  storePermanentVod: boolean
  allowLiveDVR: boolean
  allowApiVodAccess: boolean
  interactiveStreaming: boolean
  autobookingEnabled: boolean
}

export type ParsedDGELicensePeriod = Omit<FormattedDGELicensePeriod, 'startDate' | 'endDate'> & {
  startDate: Date
  endDate: Date
}

export const DGELicenseParser = z.object({
  group: z.string().optional(),
  maxEvents: z.number().optional(),
  geoListField: z.string().optional(),
  isWhiteList: z.boolean().optional(),
  createdAt: z.string().optional(),
  periods: z
    .array(
      z.object({
        id: z.number().nullable().optional(),
        version: z.string().nullable().optional(),
        purchasedEvents: z.number().nullable(),
        maxEvents: z.number().nullable(),
        start: z.string(),
        end: z.string()
      })
    )
    .nonempty(),
  isDeleted: z.boolean().optional(),
  maxBitrateAllowed: z.number().optional(),
  actualPeriod: z.boolean().nullable().optional(),
  storeVod: z.boolean().optional(),
  allowLLP: z.boolean().optional(),
  storePermanentVod: z.boolean().optional(),
  allowLiveDVR: z.boolean().optional(),
  allowApiVodAccess: z.boolean().optional(),
  interactiveStreaming: z.boolean().optional(),
  autobookingEnabled: z.boolean().optional()
})

export const PatchLicenseParser = z.object({
  data: DGELicenseParser,
  token: z.string(),
  licenseId: z.string()
})

export const PatchLicenseResponseParser = z.object({ data: DGELicenseParser })
